/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { UNITS, findUnit } from './units';

// One place that turns a product's price fields into display text, so every
// card, table and page shows "₹20 / piece" the same way.
//
// Product.price is free text: older listings hold anything from "5000" to
// "$3.80 - $4.20 / kg" or "Rs 20 per piece". Newer listings also carry
// priceCurrency (INR/USD/…) and priceUnit (kg/piece/…). A bare number or
// range gets the currency symbol and unit added; text that already names a
// currency or unit is left as written.

export const CURRENCIES = [
    { code: 'INR', symbol: '₹', label: '₹ INR' },
    { code: 'USD', symbol: '$', label: '$ USD' },
    { code: 'EUR', symbol: '€', label: '€ EUR' },
    { code: 'GBP', symbol: '£', label: '£ GBP' },
    { code: 'AED', symbol: 'AED ', label: 'AED' },
];

// Selling units, [singular, plural] — the full catalogue is in ./units.
// Free text is still allowed.
export const PRICE_UNITS = UNITS.map((x) => [x.one, x.many]);

const DETECT = [
    [/₹|\brs\.?(?=\s|\d|$)|\binr\b/i, 'INR'],
    [/\$|\busd\b/i, 'USD'],
    [/€|\beur\b/i, 'EUR'],
    [/£|\bgbp\b/i, 'GBP'],
    [/\baed\b/i, 'AED'],
];

// Explicit priceCurrency first, else a currency named inside the price text,
// else rupees (this is an Indian MSME marketplace).
export function currencyOf(product = {}) {
    if (CURRENCIES.some((c) => c.code === product.priceCurrency)) return product.priceCurrency;
    const text = String(product.price || '');
    return DETECT.find(([re]) => re.test(text))?.[1] || 'INR';
}
export const symbolOf = (code) => CURRENCIES.find((c) => c.code === code)?.symbol || '₹';

const BARE_AMOUNT = /^\s*[\d,]+(?:\.\d+)?(?:\s*(?:-|–|to)\s*[\d,]+(?:\.\d+)?)?\s*$/i;
const HAS_UNIT = /\/|\bper\b|\bkg\b|\bpcs?\b|\bpieces?\b|\bunits?\b|\bmt\b|\btonnes?\b|\blitres?\b/i;

function groupNumber(raw, code) {
    const n = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(n)) return raw;
    return n.toLocaleString(code === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: 2 });
}

export function unitLabel(unit, quantity = 1) {
    const u = String(unit || '').trim().replace(/^\/\s*/, '');
    if (!u) return '';
    const known = findUnit(u);
    if (!known) return u;
    return quantity === 1 ? known.one : known.many;
}

// "5000" + INR + piece → "₹5,000 / piece"; "10 - 20" + kg → "₹10 - ₹20 / kg";
// "$3.80 - $4.20 / kg" → unchanged. Returns '' when there's no price.
export function formatPrice(product = {}) {
    const raw = String(product.price || '').trim();
    if (!raw) return '';
    const code = currencyOf(product);
    const unit = unitLabel(product.priceUnit);
    let text = raw;
    if (BARE_AMOUNT.test(raw)) {
        const symbol = symbolOf(code);
        text = raw.split(/\s*(?:-|–|to)\s*/i).map((n) => `${symbol}${groupNumber(n, code)}`).join(' - ');
    }
    else if (/^\s*rs\.?\s*/i.test(raw)) {
        text = raw.replace(/^\s*rs\.?\s*/i, '₹');
    }
    if (unit && !HAS_UNIT.test(raw)) text += ` / ${unit}`;
    return text;
}

// "100" + kg → "100 kg"; "500" + piece → "500 pieces"; "1,000 kg" unchanged.
export function formatMoq(product = {}) {
    const raw = String(product.minOrderQty || '').trim();
    if (!raw) return '';
    if (/^[\d,]+(?:\.\d+)?$/.test(raw) && product.priceUnit) {
        const qty = Number(raw.replace(/,/g, ''));
        return `${raw} ${unitLabel(product.priceUnit, qty)}`;
    }
    return raw;
}

// Amounts for estimates and structured data: { low, high, symbol, currency }.
export function parsePriceAmounts(product = {}) {
    const amounts = (String(product.price || '').match(/\d[\d,]*(?:\.\d+)?/g) || []).slice(0, 2).map((n) => Number(n.replace(/,/g, ''))).filter((n) => n > 0);
    if (!amounts.length) return null;
    const currency = currencyOf(product);
    return { low: Math.min(...amounts), high: Math.max(...amounts), currency, symbol: symbolOf(currency) };
}
